import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useWorkspaceContext } from "../pages/ChatPage/providers";
import { Edit2, Copy, FileDown, X, Check, Menu, Palette, Printer, FileText } from "lucide-react";

export default function CoverLetterPanel({ authToken, resumeData, activeCoverLetterOverride, activeVariantId, onCreditsUpdate, coverTemplate = 'modern_ats_pro', accentColor = '#7C3AED', mode, coverLetterReloadTrigger }) {
  const {
    activeCoverLetter,
    setActiveCoverLetter,
    coverLetters,
    setCoverLetters,
    fetchCoverLetters,
    effectiveZoom = 1,
    setManualZoom,
    theme,
  } = useAppContext();

  const { setShowCustomize } = useWorkspaceContext();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const iframeRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // In preview_only mode, prefer the override prop (directly from AppContext.activeCoverLetter
  // passed via CoverLetterCanvas) over the hook value to avoid stale closure issues.
  const activeLetter = (mode === 'preview_only' && activeCoverLetterOverride !== undefined)
    ? activeCoverLetterOverride
    : activeCoverLetter;
  const setActiveLetter = setActiveCoverLetter;

  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [error, setError] = useState(null);
  const editRef = useRef(null);

  const CREDITS_COST = 3;

  useEffect(() => {
    if (authToken) fetchCoverLetters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, coverLetterReloadTrigger]);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);



  const handleGenerate = async () => {
    if (!jobDescription.trim() || !authToken || isGenerating) return;
    setIsGenerating(true);
    setError(null);
    try {
      const res = await fetch("/api/cover-letter/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          job_description: jobDescription,
          job_title: jobTitle,
          company: company,
          variant_id: activeVariantId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === "no_credits") {
          setError(`Not enough credits. Cover letter costs ${CREDITS_COST} credits.`);
        } else {
          setError(data.error || "Generation failed. Please try again.");
        }
        return;
      }
      setCoverLetters((prev) => [data, ...prev]);
      setActiveLetter(data);
      setJobDescription("");
      setJobTitle("");
      setCompany("");
      if (onCreditsUpdate && typeof data.credits_remaining === "number") {
        onCreditsUpdate(data.credits_remaining);
      }
    } catch (err) {
      setError("Network error. Please check your connection.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (letterId) => {
    if (!window.confirm("Delete this cover letter?")) return;
    setDeletingId(letterId);
    try {
      const res = await fetch(`/api/cover-letter/${letterId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setCoverLetters((prev) => prev.filter((cl) => cl.id !== letterId));
        if (activeLetter?.id === letterId) {
          const remaining = coverLetters.filter((cl) => cl.id !== letterId);
          setActiveLetter(remaining.length > 0 ? remaining[0] : null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = () => {
    if (!activeLetter) return;
    setEditContent(activeLetter.content);
    setIsEditing(true);
    setTimeout(() => editRef.current?.focus(), 50);
  };

  const handleSaveEdit = async () => {
    if (!activeLetter || isSaving) return;
    setIsSaving(true);
    try {
      const res = await fetch(`/api/cover-letter/${activeLetter.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ content: editContent }),
      });
      if (res.ok) {
        const updated = { ...activeLetter, content: editContent };
        setActiveLetter(updated);
        setCoverLetters((prev) =>
          prev.map((cl) => (cl.id === activeLetter.id ? updated : cl))
        );
        setIsEditing(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!activeLetter) return;
    try {
      await navigator.clipboard.writeText(activeLetter.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = activeLetter.content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownloadPdf = async () => {
    if (!activeLetter || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const html = buildCoverLetterHtml(activeLetter);
      const filename = `${activeLetter.company || "cover"}_letter.pdf`;
      const res = await fetch("/api/resume/pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ html, filename }),
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("PDF download failed: " + err.message);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!activeLetter || isDownloadingDocx) return;
    setIsDownloadingDocx(true);
    try {
      const filename = `${activeLetter.company || "cover"}_letter.docx`;
      const res = await fetch("/api/cover-letter/docx", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          letter_data: activeLetter,
          resume_data: resumeData,
          filename,
        }),
      });
      if (!res.ok) throw new Error("DOCX generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("DOCX download failed: " + err.message);
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  const buildCoverLetterHtml = (letter) => {
    console.log("[TRACE:HEADER] State vs Parameter:", {
      "activeCoverLetter.company": activeCoverLetter?.company,
      "activeCoverLetter.job_title": activeCoverLetter?.job_title,
      "activeCoverLetter.recipient": activeCoverLetter?.recipient,
      "letter.company": letter?.company,
      "letter.job_title": letter?.job_title,
      "letter.recipient": letter?.recipient,
      "letter.content.length": letter?.content?.length || 0,
      "letterKeys": letter ? Object.keys(letter) : []
    });

    // Identity fields: prefer this letter's own "sender" override (editable in
    // the Cover Letter Editor) — it's always available. Fall back to the
    // global resumeData, which may be null/stale if the master resume wasn't
    // loaded in this session (e.g. landing directly on a cover letter).
    const sender = letter?.sender || {};
    const ownerName  = sender.name  || resumeData?.name  || '';
    const ownerEmail = sender.email || resumeData?.email || '';
    const ownerPhone = sender.phone || resumeData?.phone || '';
    const ownerCity  = resumeData?.city  || resumeData?.location || '';

    const resolveSafeDate = (iso, formatType = "long") => {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      try {
        if (formatType === "iso-short") {
          return d.toISOString().split('T')[0];
        }
        return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
      } catch {
        return "";
      }
    };

    const rawContent = letter.content || letter.letter || '';

    // 1. Escape HTML entities first — prevents AI text from injecting arbitrary HTML
    //    into the iframe srcDoc. Must run BEFORE markdown conversion.
    const escapeHtml = (text) =>
      text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    // 2. Convert safe inline markdown to HTML tags (applied after escaping)
    const parseInlineMarkdown = (text) =>
      text
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')  // ***bold italic***
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')                // **bold**
        .replace(/__(.+?)__/g, '<strong>$1</strong>')                    // __bold__
        .replace(/\*(?!\*)(.+?)(?<!\*)\*/g, '<em>$1</em>')              // *italic*
        .replace(/_(?!_)(.+?)(?<!_)_/g, '<em>$1</em>');                 // _italic_

    const paragraphs = rawContent
      .split('\n\n')
      .map((p) => {
        const trimmed = p.trim();
        if (trimmed === '<!-- pagebreak -->' || trimmed === '<!-- page-break -->') {
          return '<div style="page-break-before: always; break-before: always; height: 0; margin: 0; padding: 0; border: none;"></div>';
        }
        return `<p>${parseInlineMarkdown(escapeHtml(p).replace(/\n/g, '<br/>'))}</p>`;
      })
      .join('');


    
    let selectedTmpl = coverTemplate;
    if (selectedTmpl === 'classic') selectedTmpl = 'academic_letter';
    if (selectedTmpl === 'harvard_classic') selectedTmpl = 'harvard_professional';
    if (selectedTmpl === 'executive_professional') selectedTmpl = 'executive_letter';
    if (selectedTmpl === 'modern_ats_pro') selectedTmpl = 'modern_professional';

    // Premium Google Fonts Import Link
    const googleFontsLink = `<link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,600;1,400&family=IBM+Plex+Serif:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Inter:wght@400;500;600;700;800&family=Lora:ital,wght@0,400;0,600;1,400&family=Manrope:wght@400;600;800&family=Merriweather:ital,wght@0,300;0,400;0,700;1,400&family=Montserrat:ital,wght@0,400;0,600;0,800;1,400&family=Outfit:wght@300;400;500;700&family=Source+Sans+3:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap" rel="stylesheet">`;

    let fontFamily = "'Georgia', serif";
    let bodyStyle = "";
    let contentHtml = "";
    const dateStr = resolveSafeDate(letter.created_at);

    if (selectedTmpl === 'harvard_professional') {
      fontFamily = "'Merriweather', 'Georgia', serif";
      bodyStyle = "font-size: 10.5pt; color: #111111; line-height: 1.6; margin: 0; padding: 25mm 20mm;";
      contentHtml = `
        <div style="text-align: center; margin-bottom: 32px;">
          <h1 style="font-size: 20pt; font-weight: 700; font-family: ${fontFamily}; text-transform: uppercase; letter-spacing: 1.5px; color: #111; margin: 0 0 8px 0;">${ownerName || "Cover Letter"}</h1>
          <div style="font-size: 9pt; color: #555; font-family: ${fontFamily}; letter-spacing: 0.5px;">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
          <div style="width: 100%; height: 1px; background-color: #222; margin-top: 14px; opacity: 0.7;"></div>
        </div>

        <div style="margin-bottom: 28px; font-size: 10.5pt; color: #222; font-family: ${fontFamily};">
          ${dateStr ? `<div style="margin-bottom: 12px; font-style: italic; color: #555;">${dateStr}</div>` : ''}
          <div style="font-weight: 700; margin-bottom: 2px;">To,</div>
          <div style="line-height: 1.5;">
            The Hiring Committee <br/>
            <strong>${letter.company || "Target Company"}</strong> <br/>
            Ref: Application for the position of <em>${letter.job_title || "Target Role"}</em>
          </div>
        </div>

        <div style="margin-bottom: 24px;">
          ${paragraphs}
        </div>
      `;
    } else if (selectedTmpl === 'modern_professional') {
      fontFamily = "'Source Sans 3', 'Inter', sans-serif";
      bodyStyle = "font-size: 10.5pt; color: #2d3748; line-height: 1.65; margin: 0; padding: 12mm 9mm;";
      contentHtml = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 36px; padding-bottom: 16px; border-bottom: 2px solid ${accentColor}40;">
          <div style="flex: 1; min-width: 0; padding-right: 16px;">
            <h1 style="font-size: 22pt; font-weight: 700; color: #1a202c; letter-spacing: -0.5px; margin: 0 0 4px 0;">${ownerName || "Candidate Name"}</h1>
            <div style="font-size: 11pt; color: ${accentColor}; font-weight: 500;">Application for ${letter.job_title || "Position"}</div>
          </div>
          <div style="text-align: right; font-size: 9.5pt; color: #4a5568; line-height: 1.45; flex-shrink: 0; min-width: 180px;">
            <div>${ownerEmail}</div>
            <div>${ownerPhone}</div>
            <div>${ownerCity}</div>
            ${dateStr ? `<div style="color: #a0aec0; margin-top: 4px;">${dateStr}</div>` : ''}
          </div>
        </div>

        <div style="margin-bottom: 24px; font-size: 10.5pt; color: #2d3748;">
          <div style="margin-bottom: 16px;">
            <strong>To:</strong> Hiring Team at <strong>${letter.company || "Company"}</strong>
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'executive_letter') {
      fontFamily = "'IBM Plex Serif', 'Georgia', serif";
      bodyStyle = "font-size: 11pt; color: #1a1a1a; line-height: 1.7; margin: 0; padding: 28mm 24mm;";
      contentHtml = `
        <div style="border-left: 5px solid ${accentColor}; padding-left: 20px; margin-bottom: 40px; padding-top: 4px; padding-bottom: 4px;">
          <h1 style="font-size: 24pt; font-weight: 700; color: #111; letter-spacing: -0.5px; margin: 0 0 6px 0; font-family: ${fontFamily};">${ownerName || "Executive Applicant"}</h1>
          <div style="font-size: 10.5pt; text-transform: uppercase; letter-spacing: 2px; color: ${accentColor}; font-weight: 600;">Confidential Executive Inquiry</div>
          <div style="font-size: 9.5pt; color: #666; margin-top: 8px; font-family: ${fontFamily};">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
        </div>

        <div style="margin-bottom: 28px; font-size: 11pt; color: #333;">
          ${dateStr ? `<div style="margin-bottom: 14px; color: #777;">${dateStr}</div>` : ''}
          <div style="margin-bottom: 4px;"><strong>To the Leadership of:</strong> ${letter.company || "Company"}</div>
          <div style="margin-bottom: 16px;"><strong>Regarding:</strong> Executive search for ${letter.job_title || "Leadership Role"}</div>
          
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'startup_letter') {
      fontFamily = "'DM Sans', 'Inter', sans-serif";
      bodyStyle = "font-size: 10.5pt; color: #2d3748; line-height: 1.6; margin: 0; padding: 20mm 20mm;";
      contentHtml = `
        <div style="margin-bottom: 30px;">
          <h1 style="font-size: 20pt; font-weight: 700; color: #1a202c; margin: 0 0 6px 0;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #718096; display: flex; gap: 12px; flex-wrap: wrap;">
            <span>${ownerEmail}</span>
            <span>&bull;</span>
            <span>${ownerPhone}</span>
            <span>&bull;</span>
            <span>${ownerCity}</span>
          </div>
          ${dateStr ? `<div style="font-size: 9pt; color: #a0aec0; margin-top: 6px;">${dateStr}</div>` : ''}
        </div>

        <div style="margin-bottom: 20px; font-size: 11pt;">
          To the team at <strong style="color: ${accentColor};">${letter.company || "Company"}</strong>,
        </div>

        <div style="margin-bottom: 24px;">
          ${paragraphs}
        </div>
      `;
    } else if (selectedTmpl === 'creative_letter') {
      fontFamily = "'Manrope', 'Inter', sans-serif";
      bodyStyle = "font-size: 10.5pt; color: #1e1b4b; line-height: 1.7; margin: 0; padding: 24mm 20mm;";
      contentHtml = `
        <div style="border-left: 4px solid ${accentColor}; padding-left: 20px; margin-left: -4px;">
          <h1 style="font-size: 24pt; font-weight: 800; color: #1e1b4b; margin: 0 0 6px 0; letter-spacing: -0.5px; text-transform: uppercase;">${ownerName}</h1>
          <div style="font-size: 10.5pt; color: ${accentColor}; font-weight: 600; margin-bottom: 12px; letter-spacing: 1px;">CREATIVE INQUIRY &bull; ${letter.job_title || "Position"}</div>
          <div style="font-size: 9pt; color: #6b7280; line-height: 1.5;">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
          ${dateStr ? `<div style="font-size: 9pt; color: #9ca3af; margin-top: 4px;">${dateStr}</div>` : ''}
        </div>

        <div style="margin-top: 36px; margin-bottom: 24px;">
          <div style="font-size: 11pt; font-weight: 600; margin-bottom: 16px; color: #1e1b4b;">
            Application directed to: ${letter.company || "Hiring Team"}
          </div>
          <div style="color: #374151;">
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'sales_letter') {
      fontFamily = "'Montserrat', 'Inter', sans-serif";
      bodyStyle = "font-size: 10pt; color: #0f172a; line-height: 1.6; margin: 0; padding: 22mm 18mm;";
      contentHtml = `
        <div style="border-top: 5px solid ${accentColor}; padding-top: 18px; margin-bottom: 32px;">
          <h1 style="font-size: 22pt; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: -0.5px; margin: 0 0 6px 0;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #475569; font-weight: 500;">
            ${ownerEmail} | ${ownerPhone} | ${ownerCity}
          </div>
          ${dateStr ? `<div style="font-size: 8.5pt; color: #94a3b8; margin-top: 6px;">Date: ${dateStr}</div>` : ''}
        </div>

        <div style="margin-bottom: 24px;">
          <div style="font-size: 11pt; font-weight: 700; color: #0f172a; margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.5px;">
            Target: Sales Representative / ${letter.job_title || "Opportunity"} at ${letter.company || "Company"}
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'government_letter') {
      fontFamily = "'Georgia', 'Times New Roman', serif";
      bodyStyle = "font-size: 11pt; color: #111111; line-height: 1.5; margin: 0; padding: 25mm 20mm;";
      contentHtml = `
        <div style="background-color: #f3f4f6; border: 1px solid #d1d5db; padding: 20px; margin-bottom: 30px;">
          <h1 style="font-size: 18pt; font-weight: bold; color: #111827; margin: 0 0 6px 0; font-family: ${fontFamily};">${ownerName || "Applicant Name"}</h1>
          <div style="font-size: 9.5pt; color: #4b5563; font-family: ${fontFamily};">
            Contact: ${ownerEmail} | Phone: ${ownerPhone} | Location: ${ownerCity}
          </div>
          ${dateStr ? `<div style="font-size: 9.5pt; color: #4b5563; margin-top: 6px; font-family: ${fontFamily};">Official Date: ${dateStr}</div>` : ''}
        </div>

        <div style="margin-bottom: 24px; font-family: ${fontFamily};">
          <div style="font-weight: bold; text-decoration: underline; margin-bottom: 14px;">
            SUBMISSION FOR ROLE: ${letter.job_title || "Official Post"} - ${letter.company || "Agency"}
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'academic_letter') {
      fontFamily = "'Times New Roman', Times, Georgia, serif";
      bodyStyle = "font-size: 11pt; color: #111111; line-height: 1.6; margin: 0; padding: 26mm 22mm;";
      contentHtml = `
        <div style="text-align: center; margin-bottom: 36px;">
          <h1 style="font-size: 21pt; font-family: ${fontFamily}; font-style: italic; font-weight: normal; margin: 0 0 6px 0; color: #000;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #444; font-family: ${fontFamily};">
            ${ownerEmail} &bull; ${ownerPhone} &bull; ${ownerCity}
          </div>
          ${dateStr ? `<div style="font-size: 9.5pt; color: #444; margin-top: 4px; font-family: ${fontFamily};">${dateStr}</div>` : ''}
        </div>

        <div style="margin-bottom: 24px; font-family: ${fontFamily}; text-align: justify;">
          <div style="margin-bottom: 20px; line-height: 1.5;">
            To the Department Chair and Selection Committee <br/>
            <strong>${letter.company || "University / Institution"}</strong>
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'product_manager_letter') {
      fontFamily = "'Inter', sans-serif";
      bodyStyle = "font-size: 10pt; color: #1f2937; line-height: 1.6; margin: 0; padding: 24mm 22mm;";
      contentHtml = `
        <div style="margin-bottom: 32px; padding-bottom: 12px; border-bottom: 3px double #e5e7eb;">
          <h1 style="font-size: 22pt; font-weight: 800; color: #111827; margin: 0 0 4px 0; letter-spacing: -0.5px;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: ${accentColor}; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Product Management & Strategy</div>
          <div style="font-size: 9pt; color: #6b7280; margin-top: 8px;">
            ${ownerEmail} &nbsp;|&nbsp; ${ownerPhone} &nbsp;|&nbsp; ${ownerCity}
          </div>
        </div>

        <div style="margin-bottom: 24px;">
          <div style="display: flex; justify-content: space-between; font-size: 9.5pt; color: #4b5563; margin-bottom: 20px;">
            <span><strong>Target:</strong> PM Team at ${letter.company || "Company"}</span>
            <span>${dateStr || ""}</span>
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'finance_letter') {
      fontFamily = "'EB Garamond', 'Georgia', serif";
      bodyStyle = "font-size: 11pt; color: #111111; line-height: 1.5; margin: 0; padding: 22mm 22mm;";
      contentHtml = `
        <div style="text-align: center; border-top: 1.5px solid #222; border-bottom: 1.5px solid #222; padding: 14px 0; margin-bottom: 32px;">
          <h1 style="font-size: 22pt; font-weight: 600; font-family: ${fontFamily}; color: #111; margin: 0 0 6px 0; letter-spacing: 0.5px;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #444; font-family: ${fontFamily}; letter-spacing: 0.5px; font-style: italic;">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
        </div>

        <div style="margin-bottom: 24px; font-family: ${fontFamily};">
          <div style="display: flex; justify-content: space-between; font-size: 10pt; color: #333; margin-bottom: 18px;">
            <span><strong>To:</strong> Recruitment Office at ${letter.company || "Firm"}</span>
            <span>${dateStr || ""}</span>
          </div>
          <div style="font-weight: 600; margin-bottom: 16px; font-size: 11pt; border-bottom: 1px solid #ddd; padding-bottom: 4px;">
            Re: Analyst Search / ${letter.job_title || "Corporate Position"}
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'internship_letter') {
      fontFamily = "'Outfit', 'Inter', sans-serif";
      bodyStyle = "font-size: 10pt; color: #334155; line-height: 1.55; margin: 0; padding: 20mm 20mm;";
      contentHtml = `
        <div style="margin-bottom: 28px;">
          <div style="display: inline-block; background-color: ${accentColor}12; border: 1px solid ${accentColor}30; padding: 4px 10px; borderRadius: 20px; font-size: 8.5pt; font-weight: 600; color: ${accentColor}; margin-bottom: 8px;">
            GRADUATE APPLICANT
          </div>
          <h1 style="font-size: 20pt; font-weight: 700; color: #0f172a; margin: 0 0 4px 0; letter-spacing: -0.5px;">${ownerName}</h1>
          <div style="font-size: 9pt; color: #64748b;">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
        </div>

        <div style="margin-bottom: 24px; font-size: 9.5pt;">
          <div style="margin-bottom: 14px; padding: 10px; background-color: #f8fafc; border-radius: 8px; border-left: 3px solid ${accentColor};">
            <strong>Applying for Internship:</strong> ${letter.job_title || "Trainee"} at <strong>${letter.company || "Company"}</strong>
            ${dateStr ? `<br/><span style="font-size: 8.5pt; color: #64748b;">Submitted: ${dateStr}</span>` : ''}
          </div>
          <div>
            ${paragraphs}
          </div>
        </div>
      `;
    } else if (selectedTmpl === 'career_change_letter') {
      fontFamily = "'Lora', 'Georgia', serif";
      bodyStyle = "font-size: 10.5pt; color: #1e293b; line-height: 1.65; margin: 0; padding: 25mm 20mm;";
      contentHtml = `
        <div style="margin-bottom: 30px;">
          <h1 style="font-size: 21pt; font-weight: 700; color: #0f172a; margin: 0 0 6px 0; font-family: ${fontFamily};">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #475569; font-family: ${fontFamily}; font-style: italic;">
            ${ownerEmail} &nbsp;&bull;&nbsp; ${ownerPhone} &nbsp;&bull;&nbsp; ${ownerCity}
          </div>
          <div style="width: 100%; height: 2px; background: linear-gradient(to right, ${accentColor}, transparent); margin-top: 12px;"></div>
        </div>

        <div style="margin-bottom: 24px;">
          <div style="font-size: 10pt; color: #64748b; font-family: ${fontFamily}; margin-bottom: 18px; line-height: 1.5;">
            <strong>Target transition role:</strong> ${letter.job_title || "New Domain"} at ${letter.company || "Target Organization"} <br/>
            ${dateStr ? `<span>Date: ${dateStr}</span>` : ''}
          </div>
          <div style="font-size: 10.5pt; font-family: ${fontFamily};">
            ${paragraphs}
          </div>
        </div>
      `;
    } else {
      // Default / Fallback
      fontFamily = "'Source Sans 3', sans-serif";
      bodyStyle = "font-size: 10.5pt; color: #2d3748; line-height: 1.6; margin: 0; padding: 20mm 20mm;";
      contentHtml = `
        <div style="margin-bottom: 30px; border-bottom: 1px solid #e2e8f0; padding-bottom: 16px;">
          <h1 style="font-size: 22pt; font-weight: 700; color: #1a202c; margin: 0 0 4px 0;">${ownerName}</h1>
          <div style="font-size: 9.5pt; color: #4a5568;">
            ${ownerEmail} &bull; ${ownerPhone} &bull; ${ownerCity}
          </div>
        </div>
        <div style="margin-bottom: 20px;">
          <strong>Company:</strong> ${letter.company || "Company"} <br/>
          <strong>Position:</strong> ${letter.job_title || "Position"}
        </div>
        <div>
          ${paragraphs}
        </div>
      `;
    }


    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  ${googleFontsLink}
  <style>
    @page { margin: 0; size: A4 portrait; }
    body { 
      font-family: ${fontFamily}; 
      ${bodyStyle}
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    .content-area { 
      font-family: ${fontFamily};
      width: 100%;
    }
    p { 
      margin: 0 0 14px 0; 
      line-height: 1.6;
    }
    strong {
      color: #000000;
      font-weight: 700;
    }
    em {
      font-style: italic;
    }
  </style>
</head>
<body>
  <div class="content-area">
    ${contentHtml}
  </div>
</body>
</html>`;
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  // [TRACE:cl] CoverLetterPanel render state
  console.log(`[TRACE:cl] CoverLetterPanel mode=${mode} activeLetter=${activeLetter ? `id=${activeLetter.id} company=${activeLetter.company} hasContent=${!!activeLetter.content}` : 'null'} resumeData.name=${resumeData?.name || 'null'}`);
  console.log("[TRACE:CL_META] CoverLetterPanel render activeLetter:", activeLetter ? {
    id: activeLetter.id,
    company: activeLetter.company,
    job_title: activeLetter.job_title,
    created_at: activeLetter.created_at,
    hasContent: !!activeLetter.content,
    contentLength: activeLetter.content?.length || 0,
    allKeys: Object.keys(activeLetter)
  } : null);
  // [TRACE:CL7]
  console.log(`[TRACE:CL7] activeCoverLetter.content length: ${activeLetter ? (activeLetter.content || '').length : 'n/a'}, hasContent: ${activeLetter ? !!activeLetter.content : 'false'}`);

  // In preview_only mode we don't need the master resume — the cover letter content
  // is self-contained. Skip the 'Resume Required' guard for this mode.
  if (!resumeData && mode !== 'preview_only') {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 32, textAlign: "center", gap: 16 }}>
        <div style={{ fontSize: 56, opacity: 0.4 }}>📝</div>
        <div style={{ fontWeight: 700, fontSize: 17, color: "var(--text-primary)" }}>Resume Required</div>
        <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: 260 }}>
          Build your resume in the chat first, then come back to generate a tailored cover letter.
        </div>
      </div>
    );
  }

  if (mode === "preview_only") {
    const isDark = document.documentElement.classList.contains('dark') || document.body.classList.contains('dark') || (typeof window !== 'undefined' && window.localStorage.getItem('theme') === 'dark');
    const themeVal = theme || (isDark ? 'dark' : 'light');

    const clGlassButtonStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      cursor: 'pointer',
      background: themeVal === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)',
      border: themeVal === 'dark' ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      color: themeVal === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
      outline: 'none',
      boxShadow: themeVal === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
      transition: 'all 0.15s ease',
    };

    const rowStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 10px',
      width: '100%',
      border: 'none',
      background: 'transparent',
      color: 'var(--text-primary)',
      fontSize: '12.5px',
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: '6px',
      textAlign: 'left',
      boxSizing: 'border-box',
      transition: 'background 120ms ease',
      outline: 'none',
    };

    const dividerStyle = {
      height: '1px',
      background: 'var(--divider)',
      margin: '4px 6px',
    };

    const handleStyleClick = () => {
      setShowCustomize(true);
      setMenuOpen(false);
    };

    const handlePrintClick = () => {
      if (iframeRef.current) {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
      }
      setMenuOpen(false);
    };

    const handleEditToggleClick = () => {
      setIsEditing(true);
      setMenuOpen(false);
    };

    const handleCopyClick = () => {
      handleCopy();
      setMenuOpen(false);
    };

    const handleDownloadPdfClick = () => {
      handleDownloadPdf();
      setMenuOpen(false);
    };

    const handleDownloadDocxClick = () => {
      handleDownloadDocx();
      setMenuOpen(false);
    };

    const handleZoomSelect = (val) => {
      if (val === 'fit-page') {
        setManualZoom(null);
      } else if (val === 'fit-width') {
        if (scrollContainerRef.current) {
          const containerWidth = scrollContainerRef.current.clientWidth - 48;
          const scale = Math.min(1.2, Math.max(0.4, containerWidth / 794));
          setManualZoom(scale);
        }
      } else {
        setManualZoom(val);
      }
    };

    const canvasWidth = 794;
    const canvasMinHeight = 1123;

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative", background: "transparent" }}>
        {activeLetter ? (
          <>
            {/* Floating Glassmorphism Action Bar */}
            <div 
              style={{ 
                position: 'absolute',
                top: 14,
                left: 16,
                right: 16,
                zIndex: 45,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxSizing: 'border-box',
              }}
            >
              {/* Right Side: Group of independent circular glass action buttons */}
              <div style={{ display: "flex", gap: 8, pointerEvents: 'auto', alignItems: 'center' }}>

                {isEditing ? (
                  <>
                    <button 
                      onClick={() => setIsEditing(false)} 
                      style={clGlassButtonStyle}
                      title="Cancel"
                      onMouseEnter={e => {
                        e.currentTarget.style.background = themeVal === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
                        e.currentTarget.style.color = themeVal === 'dark' ? '#ffffff' : '#000000';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = themeVal === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)';
                        e.currentTarget.style.color = themeVal === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
                      }}
                    >
                      <X size={15} strokeWidth={2.2} />
                    </button>
                    <button 
                      onClick={handleSaveEdit} 
                      disabled={isSaving} 
                      style={{ 
                        ...clGlassButtonStyle, 
                        background: 'var(--brand)', 
                        border: 'none',
                        color: '#ffffff'
                      }}
                      title="Save"
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <Check size={15} strokeWidth={2.5} />
                    </button>
                  </>
                ) : (
                  <>
                    {/* Menu button */}
                    <div ref={menuRef} style={{ position: 'relative' }}>
                      <button
                        onClick={() => setMenuOpen(o => !o)}
                        style={clGlassButtonStyle}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = themeVal === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
                          e.currentTarget.style.color = themeVal === 'dark' ? '#ffffff' : '#000000';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = themeVal === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)';
                          e.currentTarget.style.color = themeVal === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
                        }}
                        title="Menu"
                      >
                        <Menu size={16} strokeWidth={2.2} />
                      </button>

                      {/* Dropdown Card */}
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        background: themeVal === 'dark' ? '#0f0f18' : '#ffffff',
                        border: '1px solid var(--border-default)',
                        borderRadius: 12,
                        padding: '6px',
                        zIndex: 100,
                        width: 220,
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
                        transformOrigin: 'top right',
                        opacity: menuOpen ? 1 : 0,
                        transform: menuOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-8px)',
                        pointerEvents: menuOpen ? 'auto' : 'none',
                        transition: 'opacity 180ms ease, transform 180ms ease',
                        boxSizing: 'border-box'
                      }}>
                        {/* Section: Mode Actions */}
                        <button
                          onClick={handleEditToggleClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Edit2 size={14} color="var(--text-secondary)" />
                          <span style={{ flex: 1 }}>Edit Cover Letter</span>
                        </button>

                        <button
                          onClick={handleStyleClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Palette size={14} color="var(--text-secondary)" />
                          <span>Customize Style</span>
                        </button>

                        <button
                          onClick={handleCopyClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Copy size={14} color="var(--text-secondary)" />
                          <span>{copySuccess ? "Copied!" : "Copy Content"}</span>
                        </button>

                        <div style={dividerStyle} />

                        {/* Section: Zoom Adjustments */}
                        <div style={{ padding: '4px 8px' }}>
                          <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                            Zoom: {Math.round(effectiveZoom * 100)}%
                          </span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              onClick={() => handleZoomSelect('fit-page')}
                              style={{ flex: 1, padding: '4px 6px', fontSize: 10.5, fontWeight: 600, background: 'var(--surface-1)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', borderRadius: 4, cursor: 'pointer' }}
                            >
                              Fit Page
                            </button>
                            <button
                              onClick={() => handleZoomSelect('fit-width')}
                              style={{ flex: 1, padding: '4px 6px', fontSize: 10.5, fontWeight: 600, background: 'var(--surface-1)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', borderRadius: 4, cursor: 'pointer' }}
                            >
                              Fit Width
                            </button>
                          </div>
                          {/* Standard percentage quick links */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, gap: 4 }}>
                            {[0.5, 0.75, 1.0, 1.25].map(scale => (
                              <button
                                key={scale}
                                onClick={() => handleZoomSelect(scale)}
                                style={{
                                  fontSize: 9.5, padding: '2px 4px', border: 'none', background: 'transparent',
                                  color: effectiveZoom === scale ? 'var(--brand)' : 'var(--text-muted)',
                                  fontWeight: effectiveZoom === scale ? 700 : 500, cursor: 'pointer'
                                }}
                              >
                                {scale * 100}%
                              </button>
                            ))}
                          </div>
                        </div>

                        <div style={dividerStyle} />

                        {/* Section: Sharing and Exporting */}
                        <button
                          onClick={handlePrintClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Printer size={14} color="var(--text-secondary)" />
                          <span>Print / Share Link</span>
                        </button>

                        <div style={dividerStyle} />

                        {/* Section: Downloads */}
                        <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', margin: '4px 8px 6px' }}>
                          Download File
                        </span>

                        <button
                          onClick={handleDownloadPdfClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <FileDown size={14} color="var(--text-secondary)" />
                          <span>Download PDF</span>
                        </button>

                        <button
                          onClick={handleDownloadDocxClick}
                          style={rowStyle}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <FileText size={14} color="var(--text-secondary)" />
                          <span>Download Word (DOCX)</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Letter content scroll container with premium grid pattern */}
            <div 
              ref={scrollContainerRef}
              style={{ 
                flex: 1, 
                overflowY: "auto", 
                padding: "72px 24px 40px 24px", 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "flex-start",
                background: themeVal === 'dark' ? '#09090b' : '#f8fafc',
                backgroundImage: themeVal === 'dark'
                  ? 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)'
                  : 'radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
                scrollBehavior: 'smooth',
              }} 
              className="tg-scrollbar"
            >
              {isEditing ? (
                <div style={{ width: "100%", maxWidth: 680, height: "100%" }}>
                  <textarea
                    ref={editRef}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    style={{ width: "100%", height: "90%", padding: 24, border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "none" }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: `${canvasWidth * effectiveZoom}px`,
                    height: `${canvasMinHeight * effectiveZoom}px`,
                    position: 'relative',
                    flexShrink: 0,
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    border: "1.5px solid rgba(0,0,0,0.06)",
                    borderRadius: 8,
                    boxSizing: "border-box",
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${canvasWidth}px`,
                      height: `${canvasMinHeight}px`,
                      transform: `scale(${effectiveZoom})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    <iframe
                      ref={iframeRef}
                      title="Cover Letter Live Preview"
                      srcDoc={buildCoverLetterHtml(activeLetter)}
                      style={{ width: "100%", height: "100%", border: "none" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 56, opacity: 0.4 }}>✉️</div>
            <h3 style={{ color: "#94a3b8", fontWeight: 600, fontSize: 16 }}>No cover letter active</h3>
            <p style={{ color: "#cbd5e1", fontSize: 13, maxWidth: 300 }}>
              Ask the AI chat assistant to tailor your cover letter, and the preview will update here in real time.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--surface-0)", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid var(--border-default)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--cover-gradient)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>✉️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>Cover Letter Generator</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>AI-tailored letters · {CREDITS_COST} credits each</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Generate form */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-default)", flexShrink: 0 }}>
          <div style={{ background: "var(--surface-1)", borderRadius: 12, border: "1px solid var(--border-default)", padding: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <span>✨</span> Generate New Letter
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Target Role</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Frontend Engineer"
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface-2)", border: "1px solid var(--border-default)", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "var(--text-primary)", outline: "none" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--color-cover)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--border-default)")}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Google"
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface-2)", border: "1px solid var(--border-default)", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "var(--text-primary)", outline: "none" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--color-cover)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--border-default)")}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Job Description</label>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job description here…"
                rows={4}
                style={{ width: "100%", boxSizing: "border-box", background: "var(--surface-2)", border: "1px solid var(--border-default)", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "var(--text-primary)", outline: "none", resize: "vertical", lineHeight: 1.5 }}
                onFocus={(e) => (e.target.style.borderColor = "var(--color-cover)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--border-default)")}
              />
            </div>

            {error && (
              <div style={{ marginBottom: 10, padding: "8px 12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, fontSize: 12, color: "#f87171" }}>
                {error}
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={isGenerating || !jobDescription.trim()}
              style={{
                width: "100%",
                padding: "10px 16px",
                borderRadius: 9,
                background: isGenerating || !jobDescription.trim() ? "var(--surface-3)" : "var(--cover-gradient)",
                border: "none",
                color: isGenerating || !jobDescription.trim() ? "var(--text-muted)" : "#fff",
                fontWeight: 600,
                fontSize: 13,
                cursor: isGenerating || !jobDescription.trim() ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                transition: "opacity 0.15s",
              }}
            >
              {isGenerating ? (
                <>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "spin 0.8s linear infinite" }} />
                  Generating…
                </>
              ) : (
                <>Generate AI Cover Letter <span style={{ opacity: 0.7, fontSize: 11 }}>({CREDITS_COST} credits)</span></>
              )}
            </button>
          </div>
        </div>

        {/* History + Preview */}
        {coverLetters.length > 0 ? (
          <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* Left: History list */}
            <div style={{ width: 200, flexShrink: 0, borderRight: "1px solid var(--border-default)", overflowY: "auto", background: "var(--surface-1)" }}>
              <div style={{ padding: "10px 12px 6px", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                History ({coverLetters.length})
              </div>
              {coverLetters.map((letter) => {
                const isActive = activeLetter?.id === letter.id;
                const isDeleting = deletingId === letter.id;
                return (
                  <div
                    key={letter.id}
                    onClick={() => { setActiveLetter(letter); setIsEditing(false); }}
                    className="cl-history-item"
                    style={{
                      padding: "10px 12px",
                      margin: "2px 6px",
                      borderRadius: 8,
                      cursor: "pointer",
                      background: isActive ? "rgba(124,58,237,0.12)" : "transparent",
                      border: isActive ? "1px solid rgba(124,58,237,0.3)" : "1px solid transparent",
                      transition: "all 0.15s",
                      opacity: isDeleting ? 0.4 : 1,
                      position: "relative",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: isActive ? 600 : 500, color: isActive ? "var(--color-cover)" : "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {letter.company || "Unknown"}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {letter.job_title || "Role"}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 3 }}>
                      {formatDate(letter.created_at)}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(letter.id); }}
                      className="cl-delete-btn"
                      style={{ position: "absolute", top: 6, right: 6, background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text-muted)", opacity: 0, padding: 2, borderRadius: 4, transition: "all 0.15s" }}
                      title="Delete"
                      disabled={isDeleting}
                    >🗑</button>
                  </div>
                );
              })}
            </div>

            {/* Right: Letter preview/edit */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {activeLetter ? (
                <>
                  {/* Letter toolbar */}
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "var(--surface-1)" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>{activeLetter.company} — {activeLetter.job_title}</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 1 }}>{formatDate(activeLetter.created_at)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {isEditing ? (
                        <>
                          <button onClick={() => setIsEditing(false)} style={toolbarBtnStyle}>Cancel</button>
                          <button onClick={handleSaveEdit} disabled={isSaving} style={{ ...toolbarBtnStyle, background: "var(--cover-gradient)", color: "#fff", border: "none" }}>
                            {isSaving ? "Saving…" : "Save"}
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={handleStartEdit} style={toolbarBtnStyle} title="Edit Cover Letter">
                            ✏️ Edit
                          </button>
                          <button onClick={handleCopy} style={toolbarBtnStyle} title="Copy to Clipboard">
                            {copySuccess ? "✅" : "📋"} Copy
                          </button>
                          <button onClick={handleDownloadPdf} disabled={isDownloadingPdf} style={toolbarBtnStyle} title="Download PDF">
                            {isDownloadingPdf ? "⏳" : "⬇️"} PDF
                          </button>
                          <button onClick={handleDownloadDocx} disabled={isDownloadingDocx} style={toolbarBtnStyle} title="Download Word (DOCX)">
                            {isDownloadingDocx ? "⏳" : "📝"} Word
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Letter content */}
                  <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
                    {isEditing ? (
                      <textarea
                        ref={editRef}
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        style={{ width: "100%", height: "100%", minHeight: 400, background: "var(--surface-2)", border: "1px solid var(--color-cover)", borderRadius: 10, padding: 16, fontSize: 13, color: "var(--text-primary)", lineHeight: 1.8, resize: "vertical", outline: "none", boxSizing: "border-box" }}
                      />
                    ) : (
                      <div style={{
                        fontSize: 13,
                        color: "var(--text-primary)",
                        lineHeight: 1.85,
                        whiteSpace: "pre-wrap",
                        fontFamily: coverTemplate === 'harvard_classic' ? '"Times New Roman", Times, Georgia, serif' : coverTemplate === 'executive_professional' ? 'Calibri, Arial, sans-serif' : coverTemplate === 'faang_engineering' ? 'Consolas, Menlo, Monaco, "Courier New", monospace' : '"Inter", "Helvetica Neue", Arial, sans-serif'
                      }}>
                        {activeLetter.content}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 44, opacity: 0.3 }}>✉️</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, maxWidth: 260 }}>
              Fill in the job details above and generate your first AI-powered cover letter.
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cl-history-item:hover { background: var(--surface-2) !important; }
        .cl-history-item:hover .cl-delete-btn { opacity: 1 !important; }
        .cl-delete-btn:hover { background: rgba(239,68,68,0.15) !important; color: #ef4444 !important; }
      `}</style>
    </div>
  );
}

const toolbarBtnStyle = {
  padding: "5px 10px",
  fontSize: 12,
  borderRadius: 7,
  background: "var(--surface-2)",
  border: "1px solid var(--border-default)",
  color: "var(--text-primary)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 4,
  transition: "all 0.15s",
};
